import { useState } from 'react'
import { PageProps } from 'gatsby'
import { CustomerUpdateInput } from 'lib/graphql/storeadmin'
import { customerErrorHandling, useCustomer } from 'lib/hooks'
import { Error, SEO, showMessage } from 'components/common'
import { Editor } from 'components/customers'
import AllowDuplicateModal from 'components/customers/modals/AllowDuplicateModal'

export type DuplicateFieldsValue = {
  corporationName?: string
  corporationNameKana?: string
  firstName?: string
  lastName?: string
  zip?: string
  city?: string
  address1?: string
  address2?: string
  phone?: string
}

// フィールドエラーを要素に持つ配列を渡すと、エラーメッセージを返す関数
export const getDuplicateErrorMessages = (
  errors: string[],
  variables: DuplicateFieldsValue
): string[] => {
  const errorMessages: string[] = errors.map((error) => {
    switch (error) {
      case 'corporation_name':
        return `法人・団体名： ${variables.corporationName}`
      case 'corporation_name_kana':
        return `法人・団体名（フリガナ）： ${variables.corporationNameKana}`
      case 'last_name':
        return `担当者 姓： ${variables.lastName}`
      case 'first_name':
        return `担当者 名： ${variables.firstName}`
      case 'zip':
        return `郵便番号： ${variables.zip}`
      case 'city':
        return `市区町村： ${variables.city}`
      case 'address1':
        return `番地： ${variables.address1}`
      case 'address2':
        return `建物名：${variables.address2}`
      case 'phone':
        return `電話番号： ${variables.phone}`
      default:
        return null
    }
  })
  return errorMessages
}

export const Head = () => <SEO title="顧客（法人・団体）編集" />

const CorporateEditPage = ({
  params: { id },
}: PageProps<Queries.Query>): JSX.Element => {
  const [requesting, setRequesting] = useState(false)
  const { customer, update, error, loading } = useCustomer(id)

  // Customer Corporate error useState
  const [showCustomerErrorAlert, setShowCustomerErrorAlert] = useState(false)
  const [customerErrorDescription, setCustomerErrorDescription] = useState([])

  // モーダルの表示・非表示
  const [showModal, setShowModal] = useState(false)
  // 重複可否を選択するモーダルのonOKの引数に渡すvaluesを一時的に保存するためのステート
  const [tmpValues, setTmpValues] = useState<CustomerUpdateInput>(null)
  // 重複エラーのフィールド名を日本語の項目名に変換した文字列を格納するステート
  const [duplicateErrorMessages, setDuplicateErrorMessages] =
    useState<string[]>(null)

  // 重複可否選択モーダルのOKボタンをクリックしたときの処理
  const handleClickAllowDuplicate = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (tmpValues) onFinish(true, tmpValues)
    else showMessage('error', 'エラーが発生しました。')

    setShowModal(false)
  }

  const onFinish = async (allowDuplicate, values: CustomerUpdateInput) => {
    setRequesting(true)
    try {
      const { data, error } = await update({ allowDuplicate, input: values })
      const userErrors = data?.customerUpdate?.customerUserErrors || []
      if (userErrors.length > 0) {
        // 重複エラーがある場合
        if (userErrors.some(({ code }) => code === 'DUPLICATE_DATA_EXISTS')) {
          // 重複エラーがある場合、重複可否を選択するモーダルを表示する
          setShowModal(true)

          // フォームの入力値を保存する
          // 重複可否モーダルで「登録」を実行した時に使用する
          setTmpValues(values)

          // 重複エラーのフィールド名を取得する
          const duplicateFields = userErrors
            .filter(({ code }) => code === 'DUPLICATE_DATA_EXISTS')
            .flatMap(({ field }) => field)

          // 重複可否モーダルに表示する重複した項目の入力値のオブジェクトを作成する
          const duplicateFieldsValues: DuplicateFieldsValue = {
            corporationName: values.corporationName,
            corporationNameKana: values.corporationNameKana,
            firstName: values.firstName,
            lastName: values.lastName,
            zip: values.address.zip,
            city: values.address.city,
            address1: values.address.address1,
            address2: values.address.address2,
            phone: values.phone,
          }

          // 重複エラーのフィールド名からエラーメッセージを生成して、ステートを更新
          setDuplicateErrorMessages(
            getDuplicateErrorMessages(duplicateFields, duplicateFieldsValues)
          )
        }
        // 重複エラー以外のエラーがある場合
        else {
          setShowCustomerErrorAlert(true)
          const errorMessages = customerErrorHandling(userErrors)
          setCustomerErrorDescription(errorMessages)
          showMessage('error', 'エラーが発生しました。')
        }
      } else if (error) {
        showMessage('error', 'エラーが発生しました。')
      } else {
        setShowCustomerErrorAlert(false)
        showMessage('success', '顧客情報を編集しました。')
      }
    } catch (e) {
      setShowCustomerErrorAlert(true)
      setCustomerErrorDescription(e.message)
      showMessage('error', 'サーバーとの通信時にエラーが発生しました。')
    } finally {
      setRequesting(false)
    }
  }

  const onCloseAlert = () => {
    setShowCustomerErrorAlert(false)
  }

  if (error) {
    return Error.render(error)
  }
  return (
    <>
      <Editor
        title={customer?.corporationName}
        type="corporate"
        defaultValues={{
          phone: customer?.phone,
          note: customer?.note,
          zip: customer?.address?.zip,
          corporationName: customer?.corporationName,
          corporationNameKana: customer?.corporationNameKana,
          firstName: customer?.firstName,
          lastName: customer?.lastName,
          email: customer?.email,
          city: customer?.address?.city,
          prefectureCode: customer?.address?.prefectureCode,
          address1: customer?.address?.address1,
          address2: customer?.address?.address2,
          discount: customer?.userMetaData?.discount,
          limit: customer?.userMetaData?.limit,
          bookableFlag: customer?.bookableFlag,
          subArenaBookableFlag: customer?.metaFields.edges.some(
            ({ node }) => node.key === 'subarena-permitted'
          ),
          prefecturalGroupFlag: customer?.metaFields.edges.some(
            ({ node }) => node.key === 'local-organization'
          ),
          appliedDiscount: customer?.appliedDiscount?.discounts.edges,
        }}
        loading={loading}
        requesting={requesting}
        onFinish={onFinish}
        showAlert={showCustomerErrorAlert}
        onCloseAlert={onCloseAlert}
        alertDescription={customerErrorDescription}
      />

      {/* 重複可否選択モーダル */}
      <AllowDuplicateModal
        errorMessages={duplicateErrorMessages}
        onCancel={() => {
          setShowModal(false)
          setTmpValues(null)
        }}
        onOk={handleClickAllowDuplicate}
        okText="更新"
        open={showModal}
        loading={loading}
      />
    </>
  )
}

export default CorporateEditPage
